import React from 'react';
import { Link } from 'react-router-dom';
import { 
  FiGrid, FiArrowRight, FiUsers, FiFolder, FiSettings,
  FiCheckCircle, FiTrendingUp
} from 'react-icons/fi';
import PublicPageHeader from '../../../components/common/PublicPageHeader';
import PublicPageFooter from '../../../components/common/PublicPageFooter';
import DocsSidebar from '../../../components/docs/DocsSidebar';

const OrgDepartments = () => {
  return (
    <div className="min-h-screen bg-white dark:bg-gray-900">
      <PublicPageHeader />
      
      <div className="flex">
        <DocsSidebar currentPath="/docs/organization/departments" />
        
        <main className="flex-1 max-w-4xl mx-auto px-6 py-12">
          {/* Breadcrumb */}
          <nav className="flex items-center space-x-2 text-sm text-gray-500 dark:text-gray-400 mb-8">
            <Link to="/docs" className="hover:text-blue-600">Docs</Link>
            <span>/</span>
            <Link to="/docs/organization" className="hover:text-blue-600">Organization</Link>
            <span>/</span>
            <span className="text-gray-900 dark:text-white">Departments</span>
          </nav>

          {/* Header */}
          <div className="mb-12">
            <div className="inline-flex p-2 bg-indigo-100 dark:bg-indigo-900/30 rounded-xl mb-4">
              <FiGrid className="w-6 h-6 text-indigo-600" />
            </div>
            <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">Department Management</h1>
            <p className="text-xl text-gray-600 dark:text-gray-300">
              Organize your organization into departments for better structure and management.
            </p>
          </div>

          {/* Overview */}
          <section className="mb-16">
            <div className="bg-gradient-to-r from-indigo-50 to-purple-50 dark:from-indigo-900/20 dark:to-purple-900/20 rounded-2xl p-8">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Department Structure</h2>
              <p className="text-gray-600 dark:text-gray-300 mb-6">
                Departments help you organize teams and resources by function or business unit. 
                Each department can have its own manager, budget, and teams.
              </p>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="bg-white dark:bg-gray-800 rounded-xl p-4">
                  <h4 className="font-medium text-gray-900 dark:text-white mb-2">Department Features</h4>
                  <ul className="text-sm text-gray-600 dark:text-gray-300 space-y-1">
                    <li>• Dedicated department manager</li>
                    <li>• Department-specific budgets</li>
                    <li>• Multiple teams per department</li>
                    <li>• Department analytics</li>
                  </ul>
                </div>
                <div className="bg-white dark:bg-gray-800 rounded-xl p-4">
                  <h4 className="font-medium text-gray-900 dark:text-white mb-2">Common Departments</h4>
                  <ul className="text-sm text-gray-600 dark:text-gray-300 space-y-1">
                    <li>• Engineering / Development</li>
                    <li>• Marketing / Sales</li>
                    <li>• Human Resources</li>
                    <li>• Finance / Operations</li>
                  </ul>
                </div>
              </div>
            </div>
          </section>

          {/* Creating Departments */}
          <section className="mb-16" id="create-department">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Creating a Department</h2>
            <div className="bg-gray-50 dark:bg-gray-800 rounded-2xl p-6">
              <ol className="space-y-4">
                <li className="flex items-start space-x-3">
                  <span className="flex-shrink-0 w-6 h-6 bg-indigo-600 text-white rounded-full flex items-center justify-center text-sm">1</span>
                  <div>
                    <span className="font-medium text-gray-900 dark:text-white">Go to Organization Settings</span>
                    <p className="text-sm text-gray-600 dark:text-gray-300">Navigate to Settings → Departments</p>
                  </div>
                </li>
                <li className="flex items-start space-x-3">
                  <span className="flex-shrink-0 w-6 h-6 bg-indigo-600 text-white rounded-full flex items-center justify-center text-sm">2</span>
                  <div>
                    <span className="font-medium text-gray-900 dark:text-white">Click "Create Department"</span>
                    <p className="text-sm text-gray-600 dark:text-gray-300">Enter department name and description</p>
                  </div>
                </li>
                <li className="flex items-start space-x-3">
                  <span className="flex-shrink-0 w-6 h-6 bg-indigo-600 text-white rounded-full flex items-center justify-center text-sm">3</span>
                  <div>
                    <span className="font-medium text-gray-900 dark:text-white">Assign a Manager</span>
                    <p className="text-sm text-gray-600 dark:text-gray-300">Select a user to be the Department Manager</p>
                  </div>
                </li>
                <li className="flex items-start space-x-3">
                  <span className="flex-shrink-0 w-6 h-6 bg-indigo-600 text-white rounded-full flex items-center justify-center text-sm">4</span>
                  <div>
                    <span className="font-medium text-gray-900 dark:text-white">Set Budget (Optional)</span>
                    <p className="text-sm text-gray-600 dark:text-gray-300">Allocate a budget for the department</p>
                  </div>
                </li>
              </ol>
            </div>
          </section>

          {/* Department Manager Role */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Department Manager Role</h2>
            <div className="border border-gray-200 dark:border-gray-700 rounded-xl p-6">
              <p className="text-gray-600 dark:text-gray-300 mb-4">Department Managers have the following capabilities:</p>
              <div className="grid md:grid-cols-2 gap-3">
                <div className="flex items-center space-x-2">
                  <FiCheckCircle className="w-4 h-4 text-green-500" />
                  <span className="text-sm text-gray-700 dark:text-gray-200">Manage department settings</span>
                </div>
                <div className="flex items-center space-x-2">
                  <FiCheckCircle className="w-4 h-4 text-green-500" />
                  <span className="text-sm text-gray-700 dark:text-gray-200">Create and manage teams</span>
                </div>
                <div className="flex items-center space-x-2">
                  <FiCheckCircle className="w-4 h-4 text-green-500" />
                  <span className="text-sm text-gray-700 dark:text-gray-200">Approve budget requests</span>
                </div>
                <div className="flex items-center space-x-2">
                  <FiCheckCircle className="w-4 h-4 text-green-500" />
                  <span className="text-sm text-gray-700 dark:text-gray-200">View department analytics</span>
                </div>
                <div className="flex items-center space-x-2">
                  <FiCheckCircle className="w-4 h-4 text-green-500" />
                  <span className="text-sm text-gray-700 dark:text-gray-200">Assign team leads</span>
                </div>
                <div className="flex items-center space-x-2">
                  <FiCheckCircle className="w-4 h-4 text-green-500" />
                  <span className="text-sm text-gray-700 dark:text-gray-200">Manage department members</span>
                </div>
              </div>
            </div>
          </section>

          {/* Navigation */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Related Guides</h2>
            <div className="grid md:grid-cols-2 gap-4">
              <Link
                to="/docs/organization/teams"
                className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800 rounded-xl hover:bg-blue-50 dark:bg-blue-900/30 transition-all"
              >
                <span className="font-medium text-gray-900 dark:text-white">Team Management</span>
                <FiArrowRight className="text-gray-400 dark:text-gray-500" />
              </Link>
              <Link
                to="/docs/admin/roles"
                className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800 rounded-xl hover:bg-blue-50 dark:bg-blue-900/30 transition-all"
              >
                <span className="font-medium text-gray-900 dark:text-white">Role Hierarchy</span>
                <FiArrowRight className="text-gray-400 dark:text-gray-500" />
              </Link>
            </div>
          </section>

          {/* Footer */}
          <div className="border-t border-gray-200 dark:border-gray-700 pt-8">
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-500 dark:text-gray-400">Last updated: January 27, 2026</div>
              <div className="flex items-center space-x-4">
                <span className="text-sm text-gray-500 dark:text-gray-400">Was this helpful?</span>
                <button className="px-3 py-1 text-sm bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700">Yes</button>
                <button className="px-3 py-1 text-sm bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700">No</button>
              </div>
            </div>
          </div>
        </main>
      </div>

      <PublicPageFooter />
    </div>
  );
};

export default OrgDepartments;
